import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Filter16 } from '@carbon/icons-react';
import { Button } from 'carbon-components-react';
import Panel from './panel';
import { setNotificationHistoryFilter } from 'redux/webhook';
import { WebhookNotificationHistoryFormInitialValues } from 'types';
import { useComponentMount } from 'utilities/hooks.util';
import Utils from 'utilities/helper.util';
import { isSafari, isMobile } from 'react-device-detect';

const todayStart = Utils.getNewDate().startOf('day');
const todayEnd = Utils.getNewDate().endOf('day');

const initialValues: WebhookNotificationHistoryFormInitialValues = {
  dateRange: 'today',
  createdAfter: todayStart.utc().format(),
  createdBefore: todayEnd.utc().format(),
  status: [],
  webhook: []
};

const SearchFilter: React.FC<Props> = ({
  totalSelectedRows,
  isLoading,
  mainContainerRef
}) => {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState<Boolean>(false);
  const [filterJson, setfilterJson] = useState<string>('');
  const [
    filterValues,
    setFilterValues
  ] = useState<WebhookNotificationHistoryFormInitialValues>(initialValues);
  const [parentOverflow, setParentOverflow] = useState(false);

  const resetFilterValues = () => {
    setfilterJson(JSON.stringify(initialValues));
    setFilterValues(initialValues);
    setIsOpen(!isOpen);
  };

  const applyFilter = (values: WebhookNotificationHistoryFormInitialValues) => {
    setfilterJson(JSON.stringify(values));
    setFilterValues(values);
    setIsOpen(!isOpen);
  };

  const onClickOpenButton = () => {
    setIsOpen(!isOpen);
  };

  const checkParentOverflow = useCallback(() => {
    const mainContainerElement = mainContainerRef.current;
    if (mainContainerElement) {
      if (
        mainContainerElement.scrollHeight > mainContainerElement.clientHeight
      ) {
        setParentOverflow(true);
      }
    }
  }, [mainContainerRef, setParentOverflow]);

  useEffect(() => {
    filterJson &&
      dispatch(setNotificationHistoryFilter(JSON.parse(filterJson)));
  }, [filterJson, dispatch]);

  useEffect(() => {
    if (isOpen && !isMobile && !isSafari) {
      checkParentOverflow();
    } else {
      setParentOverflow(false);
    }
  }, [isOpen, checkParentOverflow, setParentOverflow]);

  useComponentMount(() => {
    setfilterJson(JSON.stringify(initialValues));
    setFilterValues(initialValues);
    return () => {
      dispatch(setNotificationHistoryFilter(undefined));
    };
  });

  return (
    <div
      className={
        totalSelectedRows > 0 || isLoading
          ? 'webhook-notification-history--search-filter-container hide'
          : 'webhook-notification-history--search-filter-container'
      }
    >
      <Button
        hasIconOnly
        renderIcon={Filter16}
        tooltipAlignment="center"
        tooltipPosition="bottom"
        iconDescription="Filter"
        kind="ghost"
        size="field"
        onClick={onClickOpenButton}
      />
      {isOpen && (
        <Panel
          handleFilter={applyFilter}
          filterValues={filterValues}
          resetValues={resetFilterValues}
          handleOpenCloseFilter={(open: boolean) => setIsOpen(open)}
          parentOverflow={parentOverflow}
        />
      )}
    </div>
  );
};

type Props = {
  totalSelectedRows: number;
  isLoading: boolean;
  mainContainerRef: any;
};

export default React.memo(SearchFilter);
